import { Injectable } from "@angular/core";
import { ApproveRejectNotification } from "../api/models/approveRejectNotification";
import { UserData } from "../api/models/commonUserAttributes";
import { ContactDetailsSession } from "../api/models/contactDetailsSession";
import { LocationDetailsSession } from "../api/models/locationDetailsSession";
import { NotificationDetailsSession } from "../api/models/notificationDetailsSession";
import { PlacardDetailsSession } from "../api/models/placardDetailsSession";
import { PlacardEditionRequest } from "../api/models/placardEdit";
import { TRANSACTION_SRC } from "../constants/upplm.constants";
import { PickupDetailsEditSession } from "../interface/upplm.pickupdetails.edition";
import { PlacardLocation, PlacardVerification } from "../interface/upplm.placardInformation";
import { CommonUserAttributesService } from "./upplm.commonUserAttributes.service";
import { ConfigurationService } from "./upplm.configuration.service";

@Injectable({
    providedIn: 'root'
})

export class StoredDataService {
    private _placardDetails: PlacardDetailsSession;
    private _locationDetailsSession: LocationDetailsSession;
    private _locationDetailsTransitSession: LocationDetailsSession;
    private _contactDetailsSession: ContactDetailsSession;
    private _pickupDetailsSession: PickupDetailsEditSession;
    private _notificationDetailSession: NotificationDetailsSession;
    private _placardLocationDetails: PlacardLocation;
    private _placardVerificationData: PlacardVerification;
    private _placardId: string;
    private _comesFromEditingSection: string;
    private _isEditionComplete: boolean = false;
    private _userDataInformation: UserData;
    private _requestEditedData: PlacardEditionRequest;
    private _placardStatus: string = '';
    private _placardCrisStatus: string = "";
    private _areAutoApprovalChangesAllowed: boolean = false;
    private _isLocationAndPickupEditionComplete: boolean = false;
    private _updateRequestedSaved: boolean = false;
    private _countryCode: string = 'US';
    private _gloablRegion: string = 'NA';
    private _gloablRegionForShowTimes: string = 'NA';

    constructor(private _commonUserAttributesService: CommonUserAttributesService,
        private _configurationService: ConfigurationService) {
        console.log('Hello from Stored Data Service');
    }

    set countryCode(countryCode: string) {
        this._countryCode = countryCode;
    }

    get countryCode() {
        return this._countryCode;
    }

    set globalRegion(globalRegion: string) {
        this._gloablRegion = globalRegion;
    }

    get globalRegion() {
        return this._gloablRegion;
    }

    set globalRegionForShowTimes(globalRegionForShowTimes: string) {
        this._gloablRegionForShowTimes = globalRegionForShowTimes;
    }

    get globalRegionForShowTimes() {
        return this._gloablRegionForShowTimes;
    }

    set locationDetails(locationDetailsSession: LocationDetailsSession) {
        this._locationDetailsSession = locationDetailsSession;
    }

    get locationDetails(): LocationDetailsSession {
        return this._locationDetailsSession;
    }

    set placardDetails(placardDetails: PlacardDetailsSession) {
        this._placardDetails = placardDetails;
    }

    get placardDetails(): PlacardDetailsSession {
        return this._placardDetails;
    }

    set locationDetailsTransitSession(locationDetailsTransitSession: LocationDetailsSession) {
        this._locationDetailsTransitSession = locationDetailsTransitSession;
    }

    get locationDetailsTransitSession(): LocationDetailsSession {
        return this._locationDetailsTransitSession;
    }

    set contactDetails(contactDetailsSession: ContactDetailsSession) {
        this._contactDetailsSession = contactDetailsSession;
    }

    get contactDetails(): ContactDetailsSession {
        return this._contactDetailsSession;
    }

    set notificationDetails(notificationDetailsSession: NotificationDetailsSession) {
        this._notificationDetailSession = notificationDetailsSession;
    }

    get notificationDetails(): NotificationDetailsSession {
        return this._notificationDetailSession;
    }

    set placardLocationDetails(placardLocationDetails: PlacardLocation) {
        this._placardLocationDetails = placardLocationDetails;
    }

    get placardLocationDetails() {
        return this._placardLocationDetails;
    }

    get placardId(): string {
        return this._placardId;
    }

    set placardId(placardId: string) {
        this._placardId = placardId;
    }

    get comesFromEditingSection(): string {
        return this._comesFromEditingSection;
    }

    set comesFromEditingSection(section: string) {
        this._comesFromEditingSection = section;
    }

    get isLocationAndPickupEditionComplete(): boolean {
        return this._isLocationAndPickupEditionComplete;
    }

    set isLocationAndPickupEditionComplete(value: boolean) {
        this._isLocationAndPickupEditionComplete = value;
    }

    clearEditingSections() {
        this._placardDetails = undefined;
        this.locationDetails = undefined;
        this.placardId = '';
        this.requestEditedData = undefined;
        this.userDataInformation = undefined;
        this.isEditionComplete = false;
        this.placardStatus = undefined;
        this.contactDetails = undefined;
        this.notificationDetails = undefined;
        this.pickupDetails = undefined;
        this._placardLocationDetails = undefined;
        this._locationDetailsTransitSession = undefined;
        this._placardVerificationData = undefined;
        this.globalRegion = 'NA';
        this.countryCode = 'US';
    }

    get isEditionComplete(): boolean {
        return this._isEditionComplete;
    }

    set isEditionComplete(value: boolean) {
        this._isEditionComplete = value;
    }

    get userEmail(): string {
        return this._userDataInformation.email;
    }

    get userName(): string {
        return this._userDataInformation.userId;
    }

    get transactionSource(): string {
        return TRANSACTION_SRC;
    }

    set userDataInformation(data: UserData) {
        this._userDataInformation = data;
    }

    get requestEditedData(): PlacardEditionRequest {
        return this._requestEditedData;
    }

    set requestEditedData(placardEditionRequest: PlacardEditionRequest) {
        this._requestEditedData = placardEditionRequest;
    }

    set addInternalUserNameToRequest(internalUserName: string) {
        this._requestEditedData.internalUserName = internalUserName;
    }

    get internalUserName(): string {
        return this._userDataInformation.name;
    }

    set addUserNameToRequest(userName: string) {
        this._requestEditedData.userName = userName;
    }

    set addInternalUserEmailToRequest(internalUserEmail: string) {
        this._requestEditedData.internalUserEmail = internalUserEmail;
    }

    set addChangeJustification(changeJustification: string) {
        this._requestEditedData.changeJustification = changeJustification;
    }

    set addNotifications(aproveRejectNotification: ApproveRejectNotification[]) {
        this._requestEditedData.notifications = aproveRejectNotification;
    }

    setTransactionSource() {
        this._requestEditedData.transactionSource = this.transactionSource;
    }

    get placardStatus(): string {
        return this._placardStatus;
    }

    set placardStatus(placardStatus: string) {
        this._placardStatus = placardStatus;
    }

    get placardCrisStatus() {
        return this._placardCrisStatus;
    }

    set placardCrisStatus(placardCrisStatus: string) {
        this._placardCrisStatus = placardCrisStatus;
    }

    get pickupDetails() {
        return this._pickupDetailsSession;
    }

    set pickupDetails(pickupDetailsSession: PickupDetailsEditSession) {
        this._pickupDetailsSession = pickupDetailsSession;
    }

    get areAutoApprovalChangesAllowed(): boolean {
        return this._areAutoApprovalChangesAllowed;
    }

    set areAutoApprovalChangesAllowed(areAutoApprovalChangesAllowed: boolean) {
        this._areAutoApprovalChangesAllowed = areAutoApprovalChangesAllowed;
    }

    get placardVerificationData(): PlacardVerification {
        return this._placardVerificationData;
    }

    set placardVerificationData(placardVerificationData: PlacardVerification) {
        this._placardVerificationData = placardVerificationData;
    }

    get updateRequestedSaved(): boolean {
        return this._updateRequestedSaved;
    }

    set updateRequestedSaved(updateRequestedSaved: boolean) {
        this._updateRequestedSaved = updateRequestedSaved;
    }
}